import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Confirm, StrengthBar, CopyBtn, Spinner } from '../components/UI';
import { GROUP_COLORS, ColorPicker } from '../components/UI';

const BROWSERS = ['Chrome', 'Edge', 'Firefox', 'Safari', 'Opera', 'Brave', 'Other'];
const BROWSER_ICONS = { Chrome:'🌐', Edge:'🔷', Firefox:'🦊', Safari:'🧭', Opera:'🔴', Brave:'🦁', Other:'💻' };

export default function VaultPage() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();

  const [passwords, setPasswords] = useState([]);
  const [groups,    setGroups]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [activeGrp, setActiveGrp] = useState('all');
  const [revealed,  setRevealed]  = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [delItem,   setDelItem]   = useState(null);
  const [histItem,  setHistItem]  = useState(null);

  const load = useCallback(async () => {
    try {
      const [pwRes, grpRes] = await Promise.all([
        api.get('/passwords'),
        api.get('/groups'),
      ]);
      setPasswords(pwRes.data);
      setGroups(grpRes.data);
    } catch { toast('Failed to load vault', 'error'); }
    finally  { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const groupMap = useMemo(() => Object.fromEntries(groups.map(g => [g.id, g])), [groups]);

  const filtered = useMemo(() => {
    let list = passwords;
    if (activeGrp === '__none__') list = list.filter(p => !p.group_id);
    else if (activeGrp !== 'all') list = list.filter(p => p.group_id === activeGrp);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        [p.title, p.username, p.url, p.notes].join(' ').toLowerCase().includes(q)
      );
    }
    return list;
  }, [passwords, activeGrp, search]);

  const logView = (id, title, action = 'view') =>
    api.post(`/passwords/${id}/log-view`, { action, title }).catch(() => {});

  const reveal = (id, title) => {
    setRevealed(r => ({ ...r, [id]: true }));
    logView(id, title, 'view');
  };

  const copyPw = (id, pw, title) => {
    navigator.clipboard.writeText(pw);
    toast('📋 Password copied!');
    logView(id, title, 'copy');
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text || '');
    toast(`📋 ${label} copied!`);
  };

  const openURL = (url, browser, title, id) => {
    window.open(url.startsWith('http') ? url : 'https://' + url, '_blank');
    logView(id, title, 'view');
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/passwords/${delItem.id}`);
      setPasswords(prev => prev.filter(p => p.id !== delItem.id));
      toast('🗑 Entry deleted');
    } catch { toast('Delete failed', 'error'); }
    setDelItem(null);
  };

  if (loading) return <><div className="topbar"><h2>Passwords</h2></div><div className="page"><Spinner /></div></>;

  return (
    <>
      <div className="topbar">
        <h2>Passwords</h2>
        <div className="topbar-right">
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>🔍</span>
            <input
              placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 34, width: 240 }}
            />
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditItem(null); setShowModal(true); }}>
            ＋ Add Password
          </button>
        </div>
      </div>

      <div className="page">
        {/* Group filter tabs */}
        <div className="filter-tabs">
          <div className={`ftab${activeGrp === 'all' ? ' active' : ''}`} onClick={() => setActiveGrp('all')}>
            🔑 All
            <span className="badge b-gray" style={{ padding: '1px 7px', fontSize: 10 }}>{passwords.length}</span>
          </div>
          {groups.map(g => (
            <div key={g.id} className={`ftab${activeGrp === g.id ? ' active' : ''}`} onClick={() => setActiveGrp(g.id)}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.color, display: 'inline-block' }} />
              {g.name}
              <span className="badge b-gray" style={{ padding: '1px 7px', fontSize: 10 }}>
                {passwords.filter(p => p.group_id === g.id).length}
              </span>
            </div>
          ))}
          {isAdmin && (
            <div className={`ftab${activeGrp === '__none__' ? ' active' : ''}`} onClick={() => setActiveGrp('__none__')}>
              ⚠ Unassigned
              <span className="badge b-amber" style={{ padding: '1px 7px', fontSize: 10 }}>
                {passwords.filter(p => !p.group_id).length}
              </span>
            </div>
          )}
        </div>

        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Group</th>
                <th>Title</th>
                <th>Username</th>
                <th>Password</th>
                <th>URL</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7}>
                  <div className="empty-row"><div className="ei">🔒</div>No entries found.</div>
                </td></tr>
              )}
              {filtered.map(p => {
                const grp = groupMap[p.group_id];
                const isRevealed = revealed[p.id];
                return (
                  <tr key={p.id}>
                    {/* Group */}
                    <td>
                      {grp ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: grp.color, display: 'inline-block', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{grp.name}</span>
                        </span>
                      ) : <span className="muted small">—</span>}
                    </td>

                    {/* Title + added by */}
                    <td>
                      <div style={{ fontWeight: 700 }}>{p.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--sub)' }}>by {p.created_by_name || '?'}</div>
                    </td>

                    {/* Username + copy */}
                    <td>
                      {p.username ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span className="mono" style={{ fontSize: 12 }}>{p.username}</span>
                          <CopyBtn text={p.username} label="📋" onCopied={() => toast('📋 Username copied!')} />
                        </div>
                      ) : <span className="muted">—</span>}
                    </td>

                    {/* Password + reveal + copy */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        {isRevealed
                          ? <span className="mono" style={{
                              fontSize: 12, background: 'var(--s3)', padding: '2px 7px',
                              borderRadius: 4, maxWidth: 140, overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block',
                            }}>{p.password}</span>
                          : <span style={{ fontFamily: 'var(--mono)', letterSpacing: 3, color: 'var(--sub)', fontSize: 14 }}>••••••••</span>
                        }
                        <button
                          className="icon-btn"
                          title={isRevealed ? 'Hide' : 'Reveal'}
                          onClick={() => isRevealed
                            ? setRevealed(r => ({ ...r, [p.id]: false }))
                            : reveal(p.id, p.title)
                          }
                        >{isRevealed ? '🙈' : '👁'}</button>
                        <button
                          className="icon-btn"
                          title="Copy password"
                          onClick={() => copyPw(p.id, p.password, p.title)}
                        >📋</button>
                      </div>
                    </td>
		    
		    {/* URL + open + copy */}
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.url ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <a 
                            href={p.url.startsWith('http') ? p.url : `https://${p.url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#3b82f6', textDecoration: 'none' }}
                            onClick={() => logView(p.id, p.title, 'view')}
                          >
                            {p.url}
                          </a>
                          <CopyBtn text={p.url} label="📋" onCopied={() => toast('📋 URL copied!')} />
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>

                    {/* Notes */}
                    <td style={{ maxWidth: 130, color: 'var(--sub)', fontSize: 12 }} className="truncate">
                      {p.notes || '—'}
                    </td>

                    {/* Actions */}
                    <td>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button className="icon-btn" title="Edit"
                          onClick={() => { setEditItem(p); setShowModal(true); }}>✏️</button>
                        <button className="icon-btn" title="Delete"
                          onClick={() => setDelItem(p)}>🗑️</button>
                        {isAdmin && (
                          <button className="icon-btn" title="Password History"
                            onClick={() => setHistItem(p)}>🕐</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit modal */}
      {showModal && (
        <PasswordModal
          item={editItem}
          groups={groups}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSave={async (data) => {
            try {
              if (editItem) {
                await api.put(`/passwords/${editItem.id}`, data);
                toast('✅ Entry updated');
              } else {
                await api.post('/passwords', data);
                toast('✅ Entry added');
              }
              setShowModal(false); setEditItem(null);
              load();
            } catch (e) { toast(e.response?.data?.error || 'Save failed', 'error'); }
          }}
        />
      )}

      {/* Delete confirm */}
      {delItem && (
        <Confirm
          title="Delete Entry" danger
          msg={<>Delete <strong>"{delItem.title}"</strong>? This cannot be undone.</>}
          confirmLabel="Delete"
          onCancel={() => setDelItem(null)}
          onConfirm={handleDelete}
        />
      )}

      {/* Password history (admin) */}
      {histItem && (
        <HistoryModal item={histItem} onClose={() => setHistItem(null)} />
      )}
    </>
  );
}

/* ── Password Add/Edit Modal ──────────────────────────────── */
function PasswordModal({ item, groups, onClose, onSave }) {
  const [title,    setTitle]   = useState(item?.title    || '');
  const [username, setUname]   = useState(item?.username || '');
  const [pw,       setPw]      = useState(item?.password || '');
  const [url,      setUrl]     = useState(item?.url      || '');
  const [browser,  setBrowser] = useState(item?.browser  || 'Chrome');
  const [notes,    setNotes]   = useState(item?.notes    || '');
  const [groupId,  setGroupId] = useState(item?.group_id || '');
  const [showPw,   setShowPw]  = useState(false);
  const [saving,   setSaving]  = useState(false);

  const submit = async () => {
    if (!title.trim()) { alert('Title is required.'); return; }
    if (!pw.trim())    { alert('Password is required.'); return; }
    setSaving(true);
    await onSave({ title, username, password: pw, url, browser, notes, group_id: groupId || null });
    setSaving(false);
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>{item ? 'Edit Entry ✏️' : 'Add New Entry ＋'}</h3>

        <div className="field">
          <label className="lbl">Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Gmail – Work" />
        </div>

        <div className="grid2">
          <div className="field">
            <label className="lbl">Username / Email</label>
            <input value={username} onChange={e => setUname(e.target.value)} placeholder="user@example.com" />
          </div>
          <div className="field">
            <label className="lbl">Password *</label>
            <div className="pw-wrap">
              <input
                type={showPw ? 'text' : 'password'}
                value={pw} onChange={e => setPw(e.target.value)}
                placeholder="••••••••"
              />
              <button className="pw-toggle" onClick={() => setShowPw(s => !s)}>{showPw ? '🙈' : '👁'}</button>
            </div>
            <StrengthBar password={pw} />
          </div>
        </div>

        <div className="grid2">
          <div className="field">
            <label className="lbl">URL</label>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://gmail.com" />
          </div>
          <div className="field">
            <label className="lbl">Browser</label>
            <select value={browser} onChange={e => setBrowser(e.target.value)}>
              {BROWSERS.map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
        </div>

        <div className="field">
          <label className="lbl">Group (Access Control)</label>
          <select value={groupId} onChange={e => setGroupId(e.target.value)}>
            <option value="">— No group (Admin only) —</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          {!groupId && (
            <div style={{ fontSize: 11, color: 'var(--amber)', marginTop: 5 }}>
              ⚠ Without a group, only Admins can see this entry.
            </div>
          )}
        </div>

        <div className="field">
          <label className="lbl">Notes</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional…" />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : item ? 'Save Changes' : 'Add Entry'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Password History Modal (Admin) ───────────────────────── */
function HistoryModal({ item, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    api.get(`/passwords/${item.id}/history`)
      .then(r => setHistory(r.data))
      .catch(() => toast('Failed to load history', 'error'))
      .finally(() => setLoading(false));
  }, [item.id, toast]);

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>🕐 Password History — {item.title}</h3>
        {loading ? <Spinner /> : history.length === 0 ? (
          <p style={{ color: 'var(--sub)', fontSize: 14 }}>No history yet. Passwords are recorded after first edit.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history.map((h, i) => (
              <div key={h.id} style={{
                background: 'var(--s2)', border: '1px solid var(--border2)',
                borderRadius: 8, padding: '12px 14px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--sub)' }}>
                    #{history.length - i} — Changed by <strong style={{ color: 'var(--text)' }}>{h.changed_by_name || '?'}</strong>
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--sub)' }}>
                    {new Date(h.changed_at).toLocaleString()}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="mono" style={{ fontSize: 13, background: 'var(--s3)', padding: '3px 8px', borderRadius: 5 }}>
                    {h.old_password}
                  </span>
                  <CopyBtn text={h.old_password} label="📋" />
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
